import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import SEO from '../components/common/SEO';
import SchemaMarkup from '../components/common/SchemaMarkup';
import { 
  Clock, 
  Users, 
  Star, 
  BookOpen,
  ArrowRight,
  Play,
  Award,
  CheckCircle,
  User,
  Calendar,
  Download,
  Share2,
  Heart,
  MapPin,
  Link as LinkIcon
} from 'lucide-react';
import { coursesAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/common/LoadingSpinner';
import CourseRegistration from '../components/course/CourseRegistration';
import toast from 'react-hot-toast';
import { ErrorTypes } from '../utils/errorHandler';

const CourseDetail = () => {
  const { slug } = useParams();
  const { user, isAuthenticated } = useAuth();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRegistration, setShowRegistration] = useState(false);
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        const response = await coursesAPI.getBySlug(slug);
        setCourse(response.data.data);
      } catch (error) {
        console.error('Error fetching course:', error);
        if (error.response && error.response.status === 404) {
          setError({ type: ErrorTypes.NOT_FOUND });
        } else {
          setError({ type: ErrorTypes.UNKNOWN });
        }
        toast.error('Failed to load course details');
      } finally {
        setLoading(false);
      }
    };

    fetchCourse();
  }, [slug]);
  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: course.title,
          text: course.shortDescription,
          url: `${window.location.origin}/courses/${course.slug}`,
        });
      } else {
        // Fallback
        navigator.clipboard.writeText(`${window.location.origin}/courses/${course.slug}`);
        alert("Link copied to clipboard!");
      }
    } catch (error) {
      console.error("Error sharing:", error);
    }
  };
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut'
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error?.type === ErrorTypes.NOT_FOUND || !course) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <SEO title="Course Not Found" description="The course you're looking for doesn't exist or has been removed." />
        <div className="text-center">
          <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Course Not Found
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            The course you're looking for doesn't exist or has been removed.
          </p>
          <Link to="/courses" className="btn btn-primary">
            Browse All Courses
          </Link>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <SEO title="Error" description="An error occurred while loading the course." />
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <BookOpen className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Something Went Wrong
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            We encountered an error while loading this course. Please try again later.
          </p>
          <Link to="/courses" className="btn btn-primary">
            Browse All Courses
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-16">
      <SEO 
        title={`${course.title} - Prime Edge Course`}
        description={course.shortDescription}
        keywords={`${course.category}, ${course.level} course, engineering training, ${course.title}`}
        canonicalUrl={`/courses/${slug}`}
        ogImage={course.image}
      />
      <SchemaMarkup 
        type="Course"
        courseData={{
          title: course.title,
          description: course.shortDescription,
          image: course.image,
          slug: slug,
          instructor: course.instructor
        }}
        breadcrumbData={[
          { name: 'Home', url: 'https://primeedge-solutions.com' },
          { name: 'Courses', url: 'https://primeedge-solutions.com/courses' },
          { name: course.title, url: `https://primeedge-solutions.com/courses/${slug}` }
        ]}
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-600 to-accent-600 text-white py-20">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
          >
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <span className="px-3 py-1 bg-white/20 rounded-full text-sm font-medium">
                  {course.category}
                </span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  course.level === 'beginner' 
                    ? 'bg-green-500/20 text-green-200'
                    : course.level === 'intermediate'
                    ? 'bg-yellow-500/20 text-yellow-200'
                    : 'bg-red-500/20 text-red-200'
                }`}>
                  {course.level}
                </span>
                {course.featured && (
                  <span className="px-3 py-1 bg-primary-500/20 text-primary-200 rounded-full text-sm font-medium">
                    Featured
                  </span>
                )}
              </div>

              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                {course.title}
              </h1>
              
              <p className="text-xl text-blue-100 mb-8 leading-relaxed">
                {course.shortDescription}
              </p>

              <div className="flex items-center space-x-6 mb-8">
                <div className="flex items-center space-x-2">
                  <Clock className="w-5 h-5 text-blue-200" />
                  <span className="text-blue-200">{course.duration} hours</span>
                </div>
                <div className="flex items-center space-x-2">
                  {course.courseType === 'onsite' ? (
                    <>
                      <MapPin className="w-5 h-5 text-blue-200" />
                      <span className="text-blue-200">{course.location || 'Location TBD'}</span>
                    </>
                  ) : (
                    <>
                      <LinkIcon className="w-5 h-5 text-blue-200" />
                      <span className="text-blue-200">Online Course</span>
                    </>
                  )}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <button 
                  onClick={() => setShowRegistration(true)}
                  className="btn btn-secondary btn-xl inline-flex items-center justify-center"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Register for Course
                </button>
                
                {/* <button className="btn btn-outline btn-xl inline-flex items-center border-2 border-white text-white hover:bg-white hover:text-primary-600">
                  <Play className="w-5 h-5 mr-2" />
                  Learn More
                </button> */}
              </div>
            </div>

            <div className="relative">
              <div className="relative w-full h-96 bg-white/10 rounded-2xl overflow-hidden backdrop-blur-sm">
                {course.image ? (
                  <img 
                    src={course.image} 
                    alt={course.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <BookOpen className="w-24 h-24 text-white/50" />
                  </div>
                )}
                
                <div className="absolute inset-0 flex items-center justify-center">
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-20 h-20 bg-white/90 rounded-full flex items-center justify-center"
                  >
                    <Play className="w-8 h-8 text-primary-600 ml-1" />
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <div className="container-custom py-16">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="grid grid-cols-1 lg:grid-cols-3 gap-12"
        >
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-12">
            {/* Course Description */}
            <motion.div variants={itemVariants}>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
                About This Course
              </h2>
              <div className="prose prose-lg max-w-none text-gray-600 dark:text-gray-300">
                <p>{course.description}</p>
              </div>
            </motion.div>

            {/* Learning Outcomes */}
            {course.learningOutcomes && course.learningOutcomes.length > 0 && (
              <motion.div variants={itemVariants}>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                  What You'll Learn
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {course.learningOutcomes.map((outcome, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                      <span className="text-gray-600 dark:text-gray-300">{outcome}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Prerequisites */}
            {course.prerequisites && course.prerequisites.length > 0 && (
              <motion.div variants={itemVariants}>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                  Prerequisites
                </h3>
                <div className="space-y-2">
                  {course.prerequisites.map((prereq, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-primary-600 rounded-full"></div>
                      <span className="text-gray-600 dark:text-gray-300">{prereq}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Curriculum */}
            {/* {course.curriculum && course.curriculum.length > 0 && (
              <motion.div variants={itemVariants}>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                  Course Curriculum
                </h3>
                <div className="space-y-4">
                  {course.curriculum.map((module, index) => (
                    <div key={index} className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                        Module {index + 1}: {module.title}
                      </h4>
                      <p className="text-gray-600 dark:text-gray-300 text-sm mb-2">
                        {module.description}
                      </p>
                      <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                        <span>{module.duration} minutes</span>
                        <span>{module.lessons} lessons</span>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )} */}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Course Info Card */}
            <motion.div variants={itemVariants} className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 sticky top-6">
              <div className="text-center mb-6">
                <div className="text-2xl font-bold text-primary-600 dark:text-primary-400 mb-2">
                  {course.courseType === 'onsite' ? 'On-Site Course' : 'Online Course'}
                </div>
                <div className="text-gray-500 dark:text-gray-400">{course.location || ""}</div>
              </div>

              <div className="space-y-4 mb-6">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 dark:text-gray-300">Duration</span>
                  <span className="font-medium">{course.duration} hours</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 dark:text-gray-300">Level</span>
                  <span className="font-medium capitalize">{course.level}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 dark:text-gray-300">Category</span>
                  <span className="font-medium">{course.category}</span>
                </div>
              </div>

              <button 
                onClick={() => setShowRegistration(true)}
                className="btn btn-primary btn-lg w-full mb-4"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Register for Course
              </button>

              <div className="flex space-x-2">
                {/* <button className="btn btn-outline btn-sm flex-1">
                  <Heart className="w-4 h-4 mr-2" />
                  Save
                </button> */}
                <button onClick={()=> handleShare()} className="btn btn-outline btn-sm flex-1">
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </button>
              </div>
            </motion.div>

            {/* Instructor Card */}
            {/* <motion.div variants={itemVariants} className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Instructor
              </h3>
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center">
                  <User className="w-8 h-8 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900 dark:text-white">
                    {course.instructor?.firstName} {course.instructor?.lastName}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Senior Instructor
                  </div>
                </div>
              </div>
              <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">
                {course.instructor?.bio || 'Experienced instructor with years of industry expertise.'}
              </p>
              <button className="btn btn-outline btn-sm w-full">
                View Profile
              </button>
            </motion.div> */}

            {/* Course Benefits */}
            <motion.div variants={itemVariants} className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Course Benefits
              </h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-sm text-gray-600 dark:text-gray-300">Hands-on practical training</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-sm text-gray-600 dark:text-gray-300">Certificate of completion</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-sm text-gray-600 dark:text-gray-300">Expert instructor guidance</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-sm text-gray-600 dark:text-gray-300">Real-world project experience</span>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>

      {/* Registration Modal */}
      {showRegistration && (
        <CourseRegistration
          course={course}
          onClose={() => setShowRegistration(false)}
          onSuccess={() => {
            // Optionally refresh course data or show success message
            toast.success('Registration successful!');
          }}
        />
      )}
    </div>
  );
};

export default CourseDetail;

